import React, { useEffect, useId, useMemo } from 'react';
import ReactModal from 'react-modal';

import { Props as FlexProps } from '@rover/kibble/core/Flex/Flex';
import NoScrollController from '@rover/kibble/official-patterns/Modal/utils/NoScrollController';
import type { ThemeHeadingSizes } from '@rover/kibble/styles/themeTypes';

import { ButtonVariants } from '../../core/Button';
import { ButtonSizes } from '../../core/Button/Button.common';

/** Props for the row of buttons at the bottom of a modal or sheet. */
export type DialogButtonProps = {
  /**
   * How the buttons line up. Defaults to `column`, with submit above cancel. On React Native, `row`
   * becomes `column` and `row-reverse` becomes `column-reverse`.
   */
  buttonDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  /**
   * Label for the cancel button, such as "Cancel". The button shows only when `onClickCancel` is
   * set too.
   */
  cancelButtonText?: string;
  /** Style of the cancel button. Defaults to `flat`. Give the second action `secondary`. */
  cancelButtonVariant?: ButtonVariants;
  /** Called when the cancel button is pressed. The modal stays open until `isOpen` is `false`. */
  onClickCancel?: () => void;
  /** Called when the submit button is pressed. The modal stays open until `isOpen` is `false`. */
  onClickSubmit?: () => void;
  /**
   * Label for the submit button, naming the action, such as "Delete booking". The button shows
   * only when `onClickSubmit` is set too.
   */
  submitButtonText?: string;
  /** Grays out the submit button and stops it responding. Defaults to `false`. */
  submitButtonDisabled?: boolean;
  /**
   * Shows a spinner on the submit button while its action runs. Defaults to `false`. The button
   * still calls `onClickSubmit`, so guard against a second press.
   */
  submitButtonLoading?: boolean;
  /** Style of the submit button. Defaults to `primary`. */
  submitButtonVariant?: ButtonVariants;
};

/** Props that Modal, BottomSheet and ResponsiveDialog share, with the same meaning in each. */
export type DialogCommonProps = DialogButtonProps & {
  /** The body, between the title and the buttons. */
  children: React.ReactNode;
  /** Shows the dialog. */
  isOpen: boolean;
  /**
   * Called when the reader asks to close the dialog, such as with the ✕, Escape or a press on the
   * scrim. Set `isOpen` to `false` here: a React Native BottomSheet left open can't reopen.
   */
  onRequestClose: (payload?: unknown) => void;
  /**
   * The title, which names the dialog for screen readers. Without one, they start on the subtitle
   * or the body.
   */
  title?: string;
  /** A line of text under the title. */
  subtitle?: string;
  /**
   * Names the dialog for screen readers in place of `title`, which it overrides. Without a title,
   * they start on it.
   */
  accessibilityLabel?: string;
  /**
   * Use only when the dialog button props can't cover your actions, such as a destructive button
   * or an inline error. Renders below the buttons, and scrolls with the body in a React Native sheet.
   */
  customFooter?: React.ReactNode;
  /**
   * Web-only: the app's root element, which screen readers can't reach while the dialog is open.
   * Defaults to `#base-content`.
   */
  appElementSelector?: string;
  /**
   * Lets the reader close the dialog without a button, such as with a press on the scrim. Defaults
   * to `true`. The component's guide says which ways out it turns off and whether the ✕ stays.
   */
  isDismissible?: boolean;
  /** Overrides whether the ✕ shows. The default differs by component; see its guide. */
  showCloseIcon?: boolean;
};

/** Common props for Modal across web and native */
export type ModalCommonProps = DialogCommonProps & {
  /** Size of the ✕ button. Defaults to `default`. */
  closeIconSize?: ButtonSizes;
  /** @deprecated Use `title`, which matches BottomSheet. `title` wins when both are set. */
  headerText?: string;
  /**
   * Caps the surface, so the body scrolls while the title and buttons stay in view. Web takes a CSS
   * length such as `85vh`, and native takes `n%` or `nvh` of the window. By default web grows with
   * its content, and native scrolls a body taller than 55% of the screen.
   */
  maxHeight?: string;
  /**
   * @deprecated Use `isDismissible`. Turns off only the scrim on web, and native ignores it.
   * `isDismissible={false}` also turns off Escape and Back while the ✕ is hidden.
   */
  shouldCloseOnOverlayClick?: boolean;
  /** Heading size of the title. Defaults to `300`. */
  titleSize?: ThemeHeadingSizes;
  /** @deprecated Use `titleSize`. `titleSize` wins when both are set. */
  headerSize?: ThemeHeadingSizes;
  /** Alignment of the title within its row. Centering keeps the heading semantics. */
  titleAlign?: 'left' | 'center';
  /** @deprecated Use `titleAlign`. `titleAlign` wins when both are set. */
  headerAlign?: 'left' | 'center';
  /**
   * Styles for the scrim behind the modal. They apply over the scrim's own, so they can replace its
   * color.
   */
  overlaySx?: React.CSSProperties;
  /**
   * Props for the surface that holds the title, body and buttons, applied over its own background,
   * corners, padding and widths.
   */
  contentProps?: FlexProps;
  /**
   * Props for the row that holds the title and ✕. They apply over its own layout and bottom margin.
   */
  titleProps?: FlexProps;
  /** @deprecated Use `titleProps`. `titleProps` wins when both are set. */
  headerProps?: FlexProps;
};

/** The deprecated props `resolveTitleProps` folds into their replacements. */
type DeprecatedTitleProps = 'headerText' | 'headerSize' | 'headerAlign' | 'headerProps';

/** Resolves the deprecated `header*` props into their `title*` replacements. */
export const resolveTitleProps = <T extends Partial<ModalCommonProps>>({
  title,
  headerText,
  titleSize,
  headerSize,
  titleAlign,
  headerAlign,
  titleProps,
  headerProps,
  ...rest
}: T): Omit<T, DeprecatedTitleProps> =>
  ({
    ...rest,
    title: title ?? headerText,
    titleSize: titleSize ?? headerSize,
    titleAlign: titleAlign ?? headerAlign,
    titleProps: titleProps ?? headerProps,
  } as Omit<T, DeprecatedTitleProps>);

/**
 * Web: marks where screen readers start in a dialog without a title, the subtitle or else the body,
 * so they skip the ✕.
 */
export const DIALOG_START_PROPS = { tabIndex: -1, 'data-dialog-start': true } as const;

export const focusDialogStart = (options?: ReactModal.OnAfterOpenCallbackOptions): void => {
  const contentEl = options?.contentEl;
  const active = document.activeElement;
  // react-modal opens a frame earlier, so leave focus that already moved inside, like autoFocus.
  if (!contentEl || (active !== contentEl && contentEl.contains(active))) return;
  contentEl.querySelector<HTMLElement>('[data-dialog-start]')?.focus();
};

/** react-modal treats the 0×0 portal as hiding the dialog, so Tab finds nothing to focus. */
export const showPortalToFocusTrap = (options?: ReactModal.OnAfterOpenCallbackOptions): void => {
  options?.overlayEl?.parentElement?.style.setProperty('display', 'contents');
};

// Shared so the page stays locked until the last open modal or sheet closes.
const noScroll = new NoScrollController();

export const usePreventScroll = (isOpen: boolean, modalId: string): void => {
  useEffect(() => {
    if (isOpen) {
      noScroll.on(modalId);
    } else {
      noScroll.off(modalId);
    }
    return () => {
      noScroll.off(modalId);
    };
  }, [isOpen, modalId]);
};

export const useReactModalSetup = (appElementSelector: string) => {
  // setAppElement has to be called before render or else we get react-modal errors.
  // useMemo with an empty array second arg will run only once, before render, much like componentWillMount does.
  // react-modal's internals make this method safe to call server-side.
  useMemo(() => {
    ReactModal.setAppElement(appElementSelector);
  }, []);

  const staticHeaderId = useId();
  const modalId = useId();

  return { staticHeaderId, modalId };
};
